import { useState } from "react";
import { Link } from "react-router-dom";
import { Send, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Modal } from "@/components/ui/Modal";
import { useApplicationMutations } from "../../hooks";
import { ITEM_CHANNEL_LABEL, type OfficialApplicationQueueRow } from "../../types";
import { apiErr } from "../../utils";

export function approvalChannelLabel(row: OfficialApplicationQueueRow): string {
  const label = (ITEM_CHANNEL_LABEL as Record<string, string>)[row.approval_channel]
    ?? row.approval_channel;
  return row.approval_channel_ref ? `${label} (${row.approval_channel_ref})` : label;
}

/**
 * Zatwierdzenie wniosku z kolejki = submit: pismo wychodzi na zewnątrz kanałem wskazanym
 * przy przekazaniu — zawsze z potwierdzeniem. Wspólne dla „Wnioski do akceptacji” i
 * zbiorczego „Do zatwierdzenia” dyrektora.
 */
export function ApplicationApproveModal({
  row,
  onClose,
  onDone,
}: {
  row: OfficialApplicationQueueRow;
  onClose: () => void;
  onDone?: () => void;
}) {
  const m = useApplicationMutations();
  return (
    <Modal open onClose={onClose} title={`Zatwierdź ${row.number}`} size="sm">
      <div className="space-y-3 text-sm">
        <p>
          Wniosek zostanie złożony i wyjdzie do adresata
          {row.recipient_name ? <> (<b>{row.recipient_name}</b>)</> : null}
          {" "}kanałem: <b>{approvalChannelLabel(row)}</b>.
          {row.approval_requested_by_name
            ? <> <b>{row.approval_requested_by_name}</b> dostanie powiadomienie.</>
            : null}
        </p>
        <p className="text-xs text-[var(--color-text-muted)]">
          Wysyła się kompilat widoczny w kolumnie „Pakiet”. Inny kanał wysyłki wybierzesz
          w{" "}
          <Link to={`/power-projects/wnioski/${row.id}`} className="underline">
            widoku wniosku
          </Link>.
        </p>
        {m.submit.isError && (
          <div className="text-xs text-red-600">{apiErr(m.submit.error)}</div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button
            loading={m.submit.isPending}
            onClick={() =>
              m.submit.mutate(
                {
                  id: row.id,
                  channel: row.approval_channel,
                  channelRef: row.approval_channel_ref,
                },
                { onSuccess: () => { onDone?.(); onClose(); } },
              )
            }
          >
            <Send size={13} className="mr-1" /> Zatwierdź i przekaż do wysłania
          </Button>
        </div>
      </div>
    </Modal>
  );
}

/** Zwrot do poprawy: wniosek wraca do szkicu, powód trafia do przekazującego. */
export function ApplicationReturnModal({
  row,
  onClose,
  onDone,
}: {
  row: OfficialApplicationQueueRow;
  onClose: () => void;
  onDone?: () => void;
}) {
  const m = useApplicationMutations();
  const [reviewNote, setReviewNote] = useState("");
  return (
    <Modal open onClose={onClose} title={`Zwróć do poprawy ${row.number}`} size="sm">
      <div className="space-y-3 text-sm">
        <p>
          Wniosek wróci do szkicu (edycja zostanie odblokowana)
          {row.approval_requested_by_name
            ? <>, a <b>{row.approval_requested_by_name}</b> dostanie powiadomienie</>
            : ", a przekazujący dostanie powiadomienie"}
          {" "}— razem z powodem, jeśli go wpiszesz.
        </p>
        <label className="block">
          <span className="section-label">Powód (opcjonalny)</span>
          <textarea className="input-base mt-1 w-full" rows={3} value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    placeholder="Co poprawić przed ponownym przekazaniem?" />
        </label>
        {m.rejectApproval.isError && (
          <div className="text-xs text-red-600">{apiErr(m.rejectApproval.error)}</div>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>Anuluj</Button>
          <Button
            loading={m.rejectApproval.isPending}
            onClick={() =>
              m.rejectApproval.mutate(
                { id: row.id, note: reviewNote },
                { onSuccess: () => { onDone?.(); onClose(); } },
              )
            }
          >
            <Undo2 size={13} className="mr-1" /> Zwróć do poprawy
          </Button>
        </div>
      </div>
    </Modal>
  );
}
